// src/modules/app-views/products/pages/Document/tabs/StockTab/TraceabilityContent.jsx
// *******************************************************************************
// TRACEABILITY COLLAPSE CONTENT
// *******************************************************************************
// Tracking strategy (none/serial/lot) + Unique Item switch.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const NO_SERIAL_PURPOSES = ['consumption', 'packaging', 'digital'];
const NO_TRACKING_PURPOSES = ['digital'];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TraceabilityContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tracking = Form.useWatch(['stock', 'tracking'], form) || 'none';
  const purpose = Form.useWatch(['stock', 'purpose'], form) || 'resale';
  const variantMode = Form.useWatch(['stock', 'variantMode'], form) || 'none';

  const isDigital = purpose === 'digital';
  const canTrack = !NO_TRACKING_PURPOSES.includes(purpose);
  const canSerial = canTrack && !NO_SERIAL_PURPOSES.includes(purpose);
  const canUnique = tracking === 'serial' && variantMode === 'none';

  const trackingOptions = useMemo(() => [
    { value: 'none', label: translateX('tracking_none') },
    { value: 'serial', label: translateX('tracking_serial'), disabled: !canSerial },
    { value: 'lot', label: translateX('tracking_lot'), disabled: !canTrack },
  ], [canSerial, canTrack]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleTrackingChange = value => {
    if (!form) return;
    if (value !== 'serial') {
      form.setFieldValue(['stock', 'isUnique'], false);
    }
  };

  const handleUniqueChange = value => {
    if (!form) return;
    if (value) {
      form.setFieldValue(['stock', 'variantMode'], 'none');
      form.setFieldValue(['stock', 'variantAttributes'], []);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Row gutter={ROW_GUTTER}>
      <Col xs={24} sm={12} lg={8}>
        <SimpleSegment
          name={['stock', 'tracking']}
          label="tracking"
          options={trackingOptions}
          block
          disabled={isDigital}
          onChange={handleTrackingChange}
        />
      </Col>

      {tracking === 'serial' && (
        <Col xs={12} sm={6} lg={4}>
          <SimpleSwitch
            name={['stock', 'isUnique']}
            label="unique_item"
            disabled={!canUnique}
            onChange={handleUniqueChange}
          />
        </Col>
      )}
    </Row>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TraceabilityContent;
